/* eslint-disable @typescript-eslint/no-explicit-any */
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarDismiss } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { inOutAnimation } from '@shared/animations';
import { FlashMessage } from '@shared/models/flash-message.model';
import { CommonService } from '@shared/services/common/common.service';
import { FlashMessageService } from '@shared/services/flash-message/flash-message.service';
import { LoadingService } from '@shared/services/loading/loading.service';
import { appQuery, fromAppActions } from '@shared/state';
import { fromApplicationActions } from '@shared/state/actions/application/application.actions';
import { ApplicationState } from '@shared/state/reducers/application/application.reducer';
import { authenticationQuery } from '@shared/state/selectors/authentication/authentication.selectors';
import { AppStore } from 'firebase-admin/lib/app/lifecycle';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { asyncScheduler, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, first, observeOn, switchMap, takeUntil } from 'rxjs/operators';
import { NavigationStateService } from './shared';
import { IntercomInitService } from '@shared/services/intercom-init.service';
import { ProductFruitsInitService } from '@shared/services/product-fruits-init.service';

@Component({
    selector: 'marketplace-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [inOutAnimation()],
    standalone: false
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class AppComponent implements OnInit, OnDestroy {
  currentDate: Date = new Date();
  deviceInfo: DeviceInfo;
  env: string = environment.version;
  isLoading$: Observable<boolean>;
  languageId: string;
  mobileBreakPoint: number = environment.mobileBreakpoint;
  subFeatureFlags$: Subscription;
  subscriptionList: Subscription = new Subscription();
  unsubscribeAll$: Subject<boolean>;

  constructor(
    private _navigationStateService: NavigationStateService,
    private _flashMessageService: FlashMessageService,
    private _breakPointObserver: BreakpointObserver,
    private _appStateStore: Store<ApplicationState>,
    private _deviceService: DeviceDetectorService,
    private _loadingService: LoadingService,
    private _commonService: CommonService,
    private _appStore: Store<AppStore>,
    private _route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _titleService: Title,
    private _store: Store<Store>,
    private _router: Router,
    private intercomInitService: IntercomInitService,
    private productFruitsInitService: ProductFruitsInitService
  ) {
    this._navigationStateService.init();
    this.getFeatureFlags();

    this.unsubscribeAll$ = new Subject();

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        switchMap(event => {
          let route = this._route;
          const pathSegments: string[] = [];

          while (route.firstChild) {
            route = route.firstChild;
            const path = route.routeConfig?.path;
            if (path && !path.includes(':')) {
              pathSegments.push(...path.split('/'));
            }
          }
          return combineLatest([of(event), route.data, of(pathSegments)]);
        })
      )
      .subscribe(([, data, pathSegments]: [NavigationEnd, any, string[]]) => {
        this.setTitle(data, pathSegments);
      });
  }

  ngOnInit(): void {
    this.sendUserIdToSentry();
    console.info('AddToEvent Marketplace Version: v' + this.env);

    this.isLoading$ = this._loadingService.loading$().pipe(observeOn(asyncScheduler));
    this.handleFlashMessages();
    this.handleBreakpointObserver();
    this.getDevice();
  }

  private setTitle(data, pathSegments: string[] = []) {
    const ADD_TO_EVENT = 'Add to Event';

    if (data.titleProp && data[data.titleProp].title) {
      this._titleService.setTitle(data[data.titleProp].title + ' | ' + ADD_TO_EVENT);
      return;
    }

    if (data.title) {
      this._titleService.setTitle(data.title + ' | ' + ADD_TO_EVENT);
      return;
    }

    const inferredTitle = this._inferTitleFromPathSegments(pathSegments);
    if (inferredTitle) {
      this._titleService.setTitle(inferredTitle + ' | ' + ADD_TO_EVENT);
      return;
    }

    this._titleService.setTitle(ADD_TO_EVENT);
  }

  /**
   * Falls back to a title guessed from the route config's own path segments (not the
   * URL, so dynamic params never leak in) when no route in the chain declares one —
   * e.g. 'reset-password' -> 'Reset Password'. Segments that are just view-mode
   * splits rather than real page names are excluded. If the last segment is a bare
   * action word (e.g. 'edit'), it's prefixed with the preceding segment so sibling
   * action routes don't all collapse to the same generic title, e.g.
   * ['payment-chase', 'edit'] -> 'Payment Chase Edit' rather than just 'Edit'.
   */
  private _inferTitleFromPathSegments(pathSegments: string[]): string | null {
    const NON_DESCRIPTIVE_SEGMENTS = new Set(['mobile', 'page']);
    const GENERIC_ACTION_SEGMENTS = new Set(['edit', 'new', 'add']);

    const meaningfulSegments = pathSegments.filter(segment => segment && !NON_DESCRIPTIVE_SEGMENTS.has(segment));
    const lastSegment = meaningfulSegments[meaningfulSegments.length - 1];

    if (!lastSegment) {
      return null;
    }

    const segmentsToTitle =
      meaningfulSegments.length > 1 && GENERIC_ACTION_SEGMENTS.has(lastSegment)
        ? [meaningfulSegments[meaningfulSegments.length - 2], lastSegment]
        : [lastSegment];

    return segmentsToTitle
      .map(segment =>
        segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      )
      .join(' ');
  }

  /**
   * Get information about the device used, including OS and browser details
   */
  getDevice() {
    this.deviceInfo = this._deviceService.getDeviceInfo();
    const deviceInfoState = new fromAppActions.SetDeviceInfo(this.deviceInfo);
    this._appStore.dispatch(deviceInfoState);
  }

  getFeatureFlags() {
    this.subFeatureFlags$ = this._appStateStore.select(appQuery.getFeatureFlags).subscribe({
      next: response => {
        this.subscriptionList.add(this.subFeatureFlags$);
        if (response) {
          this._commonService.publishFlags(response);
        }
      },
      error: error => {
        console.error(error);
      }
    });
  }

  sendUserIdToSentry() {
    this._store
      .select(authenticationQuery.getUser)
      .pipe(first())
      .subscribe({
        next: data => {
          if (data) {
            Sentry.setUser({
              id: data.userId,
              username: data.displayName,
              email: data.email
            });
          }
        },
        error: msg => {
          console.error(msg);
        }
      });
  }

  /**
   * Handle App wide Flash Messages
   */
  private handleFlashMessages() {
    this._flashMessageService
      .flashMessage$()
      .pipe(
        takeUntil(this.unsubscribeAll$),
        switchMap(flashMessage => {
          if (flashMessage.message) {
            return combineLatest([
              of(flashMessage),
              this._snackBar
                .open(flashMessage.message, flashMessage.action || 'Close', {
                  duration: flashMessage.timeout,
                  verticalPosition: 'top',
                  panelClass: ['snack-action']
                })
                .afterDismissed()
            ]);
          } else {
            return of([{}, {}]);
          }
        })
      )
      .subscribe(([flashMessage, event]: [FlashMessage, MatSnackBarDismiss]) => {
        if (event.dismissedByAction && flashMessage.actionCallback) {
          flashMessage.actionCallback(event);
        }
      });

    this._flashMessageService
      .isShowingFlashMessage$()
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(isShowing => {
        if (!isShowing) {
          this._snackBar.dismiss();
        }
      });
  }

  /**
   * Listen to breakPointObserver
   * MOBILE / PAGE VIEW
   */
  private handleBreakpointObserver() {
    this._breakPointObserver.observe([`(min-width: ${this.mobileBreakPoint}px)`]).subscribe((state: BreakpointState) => {
      const action = state.matches ? new fromApplicationActions.SetPageView() : new fromApplicationActions.SetMobileView();
      this._store.dispatch(action);
    });
  }

  /**
   * Destroy Application Subscriptions
   */
  public ngOnDestroy(): void {
    this.unsubscribeAll$.next(true);
    this.unsubscribeAll$.complete();
    this.subscriptionList.unsubscribe();
  }
}
